import * as React from 'react';
import { DispatchWithoutAction, FC, ReactElement, useState } from 'react';
import FormControl from '@mui/material/FormControl';
import RadioGroup from '@mui/material/RadioGroup';
import cn from 'classnames';

import { FormControlLabel, FormControlLabelClasses, Radio } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { EngineTheme, useMergeClasses, useTheme } from '../../../theme';
import { InsurancePrice } from '../InsurancePrice/InsurancePrice';
import { Money } from '../../../types';
import Tooltip from '../../../BaseComponents/Tooltip';

interface Props {
	className?: string;
	classes?: EngineTheme['InsurancePage']['SelectForm'];
	onSelect: DispatchWithoutAction;
	onRemove: DispatchWithoutAction;
	price?: Money;
	selected: boolean;
	hideForAllPassengersTextBecauseSinglePassenger?: boolean; // if there single passenger in order
	isTouched?: boolean;
	isError?: boolean;
}

interface RadioItem {
	value: string;
	classes: Partial<FormControlLabelClasses>;
	label: ReactElement;
}

export const SelectForm: FC<Props> = ({
	className,
	classes,
	selected,
	price,
	onSelect,
	onRemove,
	hideForAllPassengersTextBecauseSinglePassenger,
	isTouched,
	isError
}) => {
	const { t } = useTranslation('InsurancePage');
	const { SelectForm: defaultClasses } = useTheme('InsurancePage');
	const theme = useMergeClasses(defaultClasses, classes);

	const [value, setValue] = useState(isTouched ? `${selected}` : selected ? 'true' : null);

	const containerRef = React.useRef<HTMLDivElement>(null);

	React.useEffect(() => {
		if (isError && containerRef.current) {
			containerRef.current.scrollIntoView({
				behavior: 'smooth',
				block: 'center',
				inline: 'nearest'
			});
		}
	}, [isError]);

	const radioItems: RadioItem[] = [
		{
			value: 'true',
			classes: {
				label: theme.labelContainer
			},
			label: (
				<div className={cn(theme.label, theme.label__agreement, { [theme.labelMute]: value === 'false' })}>
					<span>{t('I want protect my trip.')}</span>
					{price && (
						<InsurancePrice
							className={theme.price}
							contentClassName={theme.priceContent}
							selected={selected}
							price={price}
							variant={'mostPopular'}
							hideForAllPassengersTextBecauseSinglePassenger={
								hideForAllPassengersTextBecauseSinglePassenger
							}
						/>
					)}
				</div>
			)
		},
		{
			value: 'false',
			classes: {
				label: theme.labelContainer
			},
			label: (
				<div className={cn(theme.label, { [theme.labelMute]: value === 'true' })}>
					{t('I do not want to insure my life or luggage and assume all risks')}
				</div>
			)
		}
	];

	return (
		<Tooltip
			classes={{
				tooltip: theme.tooltip,
				arrow: theme.tooltip_arrow,
				tooltipArrow: theme.tooltip_arrow
			}}
			open={isError}
			title={t('Please select the insurance')}
			placement="top-start"
			PopperProps={{
				disablePortal: true
			}}
		>
			<FormControl
				className={cn(className, theme.form, {
					[theme.singleTravellerInOrder]: hideForAllPassengersTextBecauseSinglePassenger,
					[theme.form_error]: isError
				})}
				error={isError}
				ref={containerRef}
			>
				<RadioGroup
					name="select-insurance-form"
					onChange={e => {
						if (e.target.value === 'true') {
							onSelect();
						} else {
							onRemove();
						}

						setValue(e.target.value);
					}}
					defaultValue={'true'}
				>
					{radioItems.map(item => (
						<FormControlLabel
							key={item.value}
							value={item.value}
							className={theme.root}
							control={
								<Radio
									className={theme.radio}
									classes={{
										checked: theme.checked
									}}
									value={item.value}
									checked={value === item.value}
								/>
							}
							classes={item.classes}
							label={item.label}
						/>
					))}
				</RadioGroup>
			</FormControl>
		</Tooltip>
	);
};
